import { useState, useCallback, useRef } from 'react';
import { apiGet } from 'src/lib/api/axios';

export interface MentionUser {
  user_id: number;
  email: string;
  firstname: string | null;
  surname: string | null;
}

export interface MentionData {
  user_id?: number;
  email?: string;
  /** The text inserted into the textarea (e.g. "Alice Smith").
   *  Used to reconcile mentions with current text; stripped before backend submission. */
  displayText?: string;
}

export interface MentionWithDisplayName extends MentionData {
  displayName?: string;
}

/** Strip frontend-only metadata before sending mentions to the API. */
export function sanitizeMentionsForApi(mentions: MentionData[]): Omit<MentionData, 'displayText'>[] {
  return mentions.map(({ displayText: _, ...rest }) => rest);
}

/** Add `@` markers before each mention's display text so saved comments can identify mentions. */
export function addMentionMarkers(text: string, mentions: MentionData[]): string {
  if (!mentions.length) return text;

  const sorted = mentions
    .filter((m) => m.displayText && text.includes(m.displayText))
    .sort((a, b) => (b.displayText?.length ?? 0) - (a.displayText?.length ?? 0));

  let result = text;
  for (const mention of sorted) {
    result = result.replace(mention.displayText!, `@${mention.displayText}`);
  }
  return result;
}

export function useMentionUsers(groupId?: string | null) {
  const [suggestions, setSuggestions] = useState<MentionUser[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const search = useCallback(
    async (query: string) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }

      if (query.length < 1) {
        setSuggestions([]);
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      try {
        let url = `/organisations/users/search?q=${encodeURIComponent(query)}`;
        if (groupId) {
          url += `&group_id=${encodeURIComponent(groupId)}`;
        }
        const { data } = await apiGet<MentionUser[]>(url, {
          signal: controller.signal,
          meta: { globalError: false, context: 'mention-user-search' }
        });
        if (!controller.signal.aborted) {
          setSuggestions(data);
        }
      } catch {
        if (!controller.signal.aborted) {
          setSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [groupId]
  );

  const clear = useCallback(() => {
    setSuggestions([]);
    setLoading(false);
  }, []);

  return { suggestions, loading, search, clear };
}
